import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../../Auth/AuthContext';
import { getAuthenticatedTicketUser } from '../../../utils/ticketUser';
import { closePdfPopup, openPdfBlob, openPdfPopup } from '../../../utils/api';
import { authenticatedSessionRequestConfig } from '../../../utils/csrf';
import { formatApiMoneyForInput, parseColombianMoneyInput } from '../../../utils/money';
import {
  buildRenewalTicketCommand,
  canonicalTicketErrorMessage,
  createTicketOperationId,
  isTicketChangedError,
} from '../../../utils/monthlyTicketCommands';
import { monthlyTicketObservationsInputValue } from '../../../utils/monthlyTicketObservations';

const API_URL = process.env.REACT_APP_API_URL;
const ROWS_PER_PAGE = 5;
const LEGACY_FINANCIAL_WARNING = 'El ticket fue actualizado. Esta renovación no tiene un movimiento financiero asociado, por lo que caja y reportes no fueron modificados.';

const canonicalTicketByRenewal = (tickets) => {
  const canonical = new Map();
  tickets.forEach((ticket) => {
    if (ticket?.renovacion !== null && ticket?.renovacion !== undefined) {
      canonical.set(Number(ticket.renovacion), ticket);
    }
  });
  return canonical;
};

const renewalDocumentDates = (renewal, canonicalTickets) => {
  const ticket = canonicalTickets.get(Number(renewal.id));
  return {
    start: ticket?.fecha_inicio ?? renewal.fecha_entrada ?? '',
    end: ticket?.fecha_fin ?? renewal.fecha_salida ?? '',
  };
};

const latestRenewalByDocumentDate = (renewals, canonicalTickets) => {
  let latestKey = '';
  let latestCandidates = [];
  renewals.forEach((candidate) => {
    const dates = renewalDocumentDates(candidate, canonicalTickets);
    const candidateKey = `${dates.end}|${dates.start}`;
    if (candidateKey > latestKey) {
      latestKey = candidateKey;
      latestCandidates = [candidate];
    } else if (candidateKey === latestKey) {
      latestCandidates.push(candidate);
    }
  });
  if (latestCandidates.length === 1) return latestCandidates[0];
  const canonicalCandidates = latestCandidates.filter(
    (candidate) => canonicalTickets.has(Number(candidate.id))
  );
  return canonicalCandidates.length === 1 ? canonicalCandidates[0] : null;
};

export default function TicketRenovacionFormOptimizado({
  openTicketRenovacionForm,
  setOpenTicketRenovacionForm,
  data,
  areUserAdmin,
  handleUpdateData,
}) {
  const { user } = useAuth();
  const submitRef = useRef(false);
  const operationIdRef = useRef(null);
  const legacyWarningPendingRef = useRef(false);
  const cacheRef = useRef({});
  const renovaciones = useMemo(
    () => (Array.isArray(data?.renovaciones) ? data.renovaciones : []),
    [data]
  );
  const tickets = useMemo(
    () => (Array.isArray(data?.ticketsRenovaciones) ? data.ticketsRenovaciones : []),
    [data]
  );
  const canonicalTickets = useMemo(
    () => canonicalTicketByRenewal(tickets),
    [tickets]
  );
  const defaultRenovacion = useMemo(
    () => latestRenewalByDocumentDate(renovaciones, canonicalTickets),
    [canonicalTickets, renovaciones]
  );
  const defaultRenovacionId = defaultRenovacion ? Number(defaultRenovacion.id) : '';
  const [selectedRenovacion, setSelectedRenovacion] = useState(defaultRenovacionId);
  const [selectedCliente, setSelectedCliente] = useState(data?.cliente?.id || null);
  const [dataSelectedCliente, setDataSelectedCliente] = useState(data?.cliente || null);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [valorPagado, setValorPagado] = useState('0,00');
  const [selectedObservaciones, setSelectedObservaciones] = useState('');
  const [ticketEdited, setTicketEdited] = useState(false);
  const [editing, setEditing] = useState(false);
  const [clientesData, setClientesData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openDialogClientes, setOpenDialogClientes] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successWarning, setSuccessWarning] = useState('');
  const [refreshPending, setRefreshPending] = useState(false);

  const selectedRenovacionData = renovaciones.find(
    (renovacion) => Number(renovacion.id) === Number(selectedRenovacion)
  ) || null;
  const issuedTicket = canonicalTickets.get(Number(selectedRenovacion)) || null;

  useEffect(() => {
    if (!openTicketRenovacionForm) return;
    setSelectedRenovacion(defaultRenovacionId);
    setSelectedCliente(data?.cliente?.id || null);
    setDataSelectedCliente(data?.cliente || null);
    setEditing(false);
    setTicketEdited(false);
    operationIdRef.current = null;
    setErrorMessage('');
    if (legacyWarningPendingRef.current) {
      setSuccessWarning(LEGACY_FINANCIAL_WARNING);
      legacyWarningPendingRef.current = false;
    } else {
      setSuccessWarning('');
    }
    setRefreshPending(false);
  }, [data, defaultRenovacionId, openTicketRenovacionForm]);

  useEffect(() => {
    if (!openTicketRenovacionForm || !selectedRenovacionData) return;
    setEditing(false);
    setTicketEdited(false);
    operationIdRef.current = null;
    if (issuedTicket) {
      setFechaInicio(
        issuedTicket.fecha_inicio ?? selectedRenovacionData.fecha_entrada ?? ''
      );
      setFechaFin(
        issuedTicket.fecha_fin ?? selectedRenovacionData.fecha_salida ?? ''
      );
      setValorPagado(
        formatApiMoneyForInput(parseColombianMoneyInput(issuedTicket.valor))
      );
      setSelectedObservaciones(issuedTicket.observaciones ?? '');
      setSelectedCliente(null);
      setDataSelectedCliente({ nombre: issuedTicket.nombre_cliente, apellidos: '' });
      return;
    }
    setFechaInicio(selectedRenovacionData.fecha_entrada ?? '');
    setFechaFin(selectedRenovacionData.fecha_salida ?? '');
    setValorPagado(formatApiMoneyForInput(selectedRenovacionData.valor));
    setSelectedObservaciones(selectedRenovacionData.observaciones ?? '');
    setSelectedCliente(data?.cliente?.id || null);
    setDataSelectedCliente(data?.cliente || null);
  }, [data, issuedTicket, openTicketRenovacionForm, selectedRenovacionData]);

  const restoreIssuedTicket = () => {
    if (!issuedTicket) return;
    setFechaInicio(
      issuedTicket.fecha_inicio ?? selectedRenovacionData?.fecha_entrada ?? ''
    );
    setFechaFin(
      issuedTicket.fecha_fin ?? selectedRenovacionData?.fecha_salida ?? ''
    );
    setValorPagado(
      formatApiMoneyForInput(parseColombianMoneyInput(issuedTicket.valor))
    );
    setSelectedObservaciones(issuedTicket.observaciones ?? '');
    setSelectedCliente(null);
    setDataSelectedCliente({ nombre: issuedTicket.nombre_cliente, apellidos: '' });
    setTicketEdited(false);
  };

  useEffect(() => {
    if (!openDialogClientes) return undefined;
    let mounted = true;
    const fetchClientes = async () => {
      const cacheKey = `${searchText}|${currentPage}`;
      if (cacheRef.current[cacheKey]) {
        setClientesData(cacheRef.current[cacheKey].results);
        setTotalPages(Math.max(1, Math.ceil(cacheRef.current[cacheKey].count / ROWS_PER_PAGE)));
        return;
      }
      setLoadingClientes(true);
      try {
        const response = await axios.get(`${API_URL}/clientes/`, {
          params: { search: searchText, page: currentPage, page_size: ROWS_PER_PAGE },
        });
        if (!mounted) return;
        const results = response.data?.results || [];
        const count = response.data?.count || 0;
        cacheRef.current[cacheKey] = { results, count };
        setClientesData(results);
        setTotalPages(Math.max(1, Math.ceil(count / ROWS_PER_PAGE)));
      } catch (error) {
        if (mounted) setErrorMessage(canonicalTicketErrorMessage(error));
      } finally {
        if (mounted) setLoadingClientes(false);
      }
    };
    fetchClientes();
    return () => { mounted = false; };
  }, [currentPage, openDialogClientes, searchText]);

  const handleCrearTicket = async () => {
    if (submitRef.current || loading) return;
    let pdfWindow = null;
    let commandSucceeded = false;
    try {
      getAuthenticatedTicketUser(user);
      const operation = issuedTicket ? (editing ? 'replace' : 'reprint') : 'issue';
      const snapshot = operation === 'reprint' ? undefined : {
        cliente_id: selectedCliente,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        valor: parseColombianMoneyInput(valorPagado),
        observaciones: selectedObservaciones,
      };
      if (operation === 'replace' && !operationIdRef.current) {
        operationIdRef.current = createTicketOperationId();
      }
      const command = buildRenewalTicketCommand(
        data?.id,
        selectedRenovacionData?.id,
        operation,
        snapshot,
        operation === 'replace' ? {
          expectedTicketId: issuedTicket.id,
          operationId: operationIdRef.current,
        } : undefined
      );
      pdfWindow = openPdfPopup();
      if (!pdfWindow) return;
      submitRef.current = true;
      setLoading(true);
      setErrorMessage('');
      const requestConfig = await authenticatedSessionRequestConfig(API_URL, axios.get);
      const response = await axios.post(
        `${API_URL}/tickets-renovacion/`,
        command,
        requestConfig
      );
      if (![200, 201].includes(response.status) || !response.data?.id) {
        throw new Error('El servidor no devolvió el ticket.');
      }
      commandSucceeded = true;
      const skippedLegacyFinancialSync = (
        response.data.financial_sync === 'skipped_legacy_missing_movement'
      );
      operationIdRef.current = null;
      setEditing(false);
      setTicketEdited(false);
      try {
        const pdfResponse = await axios.get(
          `${API_URL}/tickets-renovacion/${response.data.id}/`,
          { responseType: 'blob', withCredentials: true }
        );
        openPdfBlob(new Blob([pdfResponse.data], { type: 'application/pdf' }), pdfWindow);
      } catch (pdfError) {
        closePdfPopup(pdfWindow);
        if (skippedLegacyFinancialSync) {
          setSuccessWarning(`${LEGACY_FINANCIAL_WARNING} El PDF no pudo abrirse.`);
        } else {
          setSuccessWarning('El nuevo ticket fue generado, pero no se pudo abrir el PDF. Actualiza los datos antes de continuar.');
        }
        setRefreshPending(true);
        return;
      }
      try {
        if (skippedLegacyFinancialSync) legacyWarningPendingRef.current = true;
        if (handleUpdateData) await handleUpdateData();
        if (skippedLegacyFinancialSync) {
          setSuccessWarning(LEGACY_FINANCIAL_WARNING);
        } else {
          setOpenTicketRenovacionForm(false);
        }
      } catch (refreshError) {
        setSuccessWarning('El nuevo ticket fue generado y el PDF se abrió, pero no fue posible actualizar la pantalla.');
        setRefreshPending(true);
      }
    } catch (error) {
      if (!commandSucceeded) closePdfPopup(pdfWindow);
      if (isTicketChangedError(error)) {
        operationIdRef.current = null;
        setEditing(false);
        setTicketEdited(false);
        try {
          if (handleUpdateData) await handleUpdateData();
        } catch (refreshError) {
          setRefreshPending(true);
        }
      }
      setErrorMessage(canonicalTicketErrorMessage(error));
    } finally {
      submitRef.current = false;
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={openTicketRenovacionForm}
        onClose={() => setOpenTicketRenovacionForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Ticket de Renovación</DialogTitle>
        <DialogContent>
          {errorMessage && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage}</Alert>}
          {successWarning && <Alert severity="warning" sx={{ mt: 1 }}>{successWarning}</Alert>}
          {issuedTicket && !editing && (
            <Alert severity="info" sx={{ mt: 1 }}>
              <strong>Reimprimir</strong>
              <br />
              Vuelve a imprimir exactamente el ticket vigente.
              <br />
              <br />
              <strong>Modificar ticket</strong>
              <br />
              Carga los datos actuales de la renovación para generar una nueva versión del ticket.
            </Alert>
          )}
          {editing && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Se cargaron los datos actuales de la renovación.
              <br />
              Al confirmar se generará un nuevo ticket.
              <br />
              El ticket anterior permanecerá disponible en el historial.
            </Alert>
          )}
          <InputLabel sx={{ pt: 1 }}>Renovación para hacer Ticket</InputLabel>
          <Select
            fullWidth
            value={selectedRenovacion}
            onChange={(event) => setSelectedRenovacion(Number(event.target.value))}
            disabled={loading || editing}
          >
            {renovaciones.map((renovacion) => (
              <MenuItem key={renovacion.id} value={Number(renovacion.id)}>
                {(() => {
                  const ticket = canonicalTickets.get(Number(renovacion.id));
                  const dates = renewalDocumentDates(renovacion, canonicalTickets);
                  return `entrada: ${dates.start} salida: ${dates.end} valor: ${ticket?.valor ?? renovacion.valor} usuario: ${ticket?.nombre_usuario ?? renovacion.nombre_usuario ?? renovacion.usuario ?? '—'}`;
                })()}
              </MenuItem>
            ))}
          </Select>

          <Typography variant="body1" gutterBottom sx={{ pt: 2 }}><strong>Empleado:</strong> {issuedTicket?.nombre_usuario ?? selectedRenovacionData?.nombre_usuario ?? selectedRenovacionData?.usuario ?? user?.nombre ?? '—'}</Typography>
          <Typography variant="body1" gutterBottom><strong>Fecha Inicio:</strong> {fechaInicio || '—'}</Typography>
          <Typography variant="body1" gutterBottom><strong>Fecha Final:</strong> {fechaFin || '—'}</Typography>

          <InputLabel sx={{ pt: 1 }}>Cliente</InputLabel>
          <TextField
            label="Cliente"
            fullWidth
            value={dataSelectedCliente ? `${dataSelectedCliente.nombre} ${dataSelectedCliente.apellidos || ''}`.trim() : ''}
            disabled
          />
          <Button
            variant="outlined"
            sx={{ mt: 1 }}
            onClick={() => setOpenDialogClientes(true)}
            disabled={!areUserAdmin || loading || (Boolean(issuedTicket) && !editing)}
          >
            Buscar Cliente
          </Button>

          <TextField
            label="Valor a Pagar"
            variant="outlined"
            type="text"
            inputMode="decimal"
            fullWidth
            margin="normal"
            value={(() => {
                return valorPagado;
            })()}
            onChange={(event) => {
              setValorPagado(event.target.value);
              setTicketEdited(true);
            }}
            disabled={!areUserAdmin || loading || (Boolean(issuedTicket) && !editing)}
          />
          {!areUserAdmin && issuedTicket && (
            <Typography variant="caption" color="text.secondary" display="block">
              Solo los administradores pueden modificar este ticket.
            </Typography>
          )}
          <InputLabel sx={{ pt: 2 }}>Observaciones</InputLabel>
          <TextField
            autoFocus
            fullWidth
            value={(() => {
                return monthlyTicketObservationsInputValue(selectedObservaciones);
            })()}
            placeholder="Nada"
            onChange={(event) => {
              setSelectedObservaciones(event.target.value);
              setTicketEdited(true);
            }}
            inputProps={{ maxLength: 200 }}
            disabled={loading || (Boolean(issuedTicket) && !editing)}
          />
          {refreshPending ? (
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              disabled={loading}
              onClick={async () => {
                setLoading(true);
                try {
                  if (handleUpdateData) await handleUpdateData();
                  setRefreshPending(false);
                  setOpenTicketRenovacionForm(false);
                } catch (error) {
                  setErrorMessage('No fue posible actualizar la pantalla. Intenta nuevamente.');
                } finally {
                  setLoading(false);
                }
              }}
            >
              Actualizar datos
            </Button>
          ) : issuedTicket && !editing ? (
            <>
              <Button variant="contained" onClick={handleCrearTicket} disabled={loading} sx={{ mt: 2 }}>
                {loading ? <CircularProgress size={24} /> : 'Reimprimir'}
              </Button>
              {areUserAdmin && !issuedTicket.anulado && (
                <Button
                  variant="outlined"
                  disabled={loading}
                  sx={{ mt: 2, ml: 1 }}
                  onClick={() => {
                    operationIdRef.current = createTicketOperationId();
                    setFechaInicio(selectedRenovacionData.fecha_entrada ?? '');
                    setFechaFin(selectedRenovacionData.fecha_salida ?? '');
                    setValorPagado(formatApiMoneyForInput(
                      selectedRenovacionData.valorTicketModificado
                      || selectedRenovacionData.valor
                    ));
                    setSelectedObservaciones(selectedRenovacionData.observaciones);
                    setSelectedCliente(data?.cliente?.id || null);
                    setDataSelectedCliente(data?.cliente || null);
                    setEditing(true);
                    setTicketEdited(true);
                    setErrorMessage('');
                  }}
                >
                  Modificar ticket
                </Button>
              )}
            </>
          ) : issuedTicket && editing ? (
            <>
              <Button
                variant="contained"
                onClick={handleCrearTicket}
                disabled={loading || !ticketEdited}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Confirmar'}
              </Button>
              {!ticketEdited && !loading && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Modifique al menos un campo para confirmar.
                </Typography>
              )}
              <Button
                variant="outlined"
                disabled={loading}
                sx={{ mt: 2, ml: 1 }}
                onClick={() => {
                  restoreIssuedTicket();
                  setEditing(false);
                  operationIdRef.current = null;
                  setErrorMessage('');
                }}
              >
                Cancelar
              </Button>
            </>
          ) : null}
          {!issuedTicket && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleCrearTicket}
            disabled={loading || !selectedRenovacionData}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Imprimir Ticket de Renovación'}
          </Button>
          )}
          {!issuedTicket && !selectedRenovacionData && (
            <Typography variant="caption" color="text.secondary" display="block">
              La mensualidad aún no tiene renovaciones.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialogClientes}
        onClose={() => setOpenDialogClientes(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Buscar Cliente</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Buscar cliente..."
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value);
              setCurrentPage(1);
            }}
            sx={{ mb: 2 }}
          />
          {loadingClientes ? <Typography>Cargando...</Typography> : clientesData.map((cliente) => (
            <div
              key={cliente.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                setSelectedCliente(cliente.id);
                setDataSelectedCliente(cliente);
                setTicketEdited(true);
                setOpenDialogClientes(false);
              }}
              onKeyDown={() => {}}
              style={{
                padding: 10,
                borderBottom: '1px solid #ccc',
                cursor: 'pointer',
                backgroundColor: selectedCliente === cliente.id ? '#1976d2' : 'transparent',
                color: selectedCliente === cliente.id ? 'white' : 'inherit',
              }}
            >
              <strong>{cliente.nombre} {cliente.apellidos}</strong><br />
              {cliente.tipo_documento}: {cliente.documento}
            </div>
          ))}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
              <Button disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>Anterior</Button>
              <Typography sx={{ mx: 2 }}>{currentPage} / {totalPages}</Typography>
              <Button disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => page + 1)}>Siguiente</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
